/* eslint-disable import/no-unresolved */
import React, { useCallback } from 'react';

import IconMC from '../../../../../../../../components/IconMC';

const floatingFieldClass =
  'tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-[#F7F8FD] tw-px-3 tw-py-2.5 tw-text-sm tw-text-gray-800 tw-outline-none focus:tw-border-gray-300';

const AiDescriptionToggle = ({ active, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-size-9 tw-rounded-lg tw-border tw-transition-colors ${
      active
        ? 'tw-border-purple-200 tw-bg-purple-50 tw-text-purple-600'
        : 'tw-border-gray-200 tw-bg-white tw-text-gray-400 hover:tw-border-purple-200 hover:tw-text-purple-500'
    }`}
    aria-pressed={active}
    aria-label={title}
  >
    <IconMC name="mia-bot" size="sm" />
  </button>
);

const RetailerApiRequestRowsEditor = ({
  t,
  rows,
  onChange,
  emptyRow,
  idPrefix,
  labels,
  hideSectionLabel = false
}) => {
  const updateRow = useCallback((rowId, partial) => {
    onChange(rows.map((row) => (row.id === rowId ? { ...row, ...partial } : row)));
  }, [rows, onChange]);

  const removeRow = useCallback((rowId) => {
    const next = rows.filter((row) => row.id !== rowId);
    onChange(next.length > 0 ? next : [emptyRow()]);
  }, [rows, onChange, emptyRow]);

  const addRow = useCallback(() => {
    onChange([...rows, emptyRow()]);
  }, [rows, onChange, emptyRow]);

  const toggleDescription = useCallback((rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    const nextActive = !row.showDescription;
    updateRow(rowId, {
      showDescription: nextActive,
      value: nextActive ? '' : row.value,
      description: nextActive ? row.description : ''
    });
  }, [rows, updateRow]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {!hideSectionLabel && (
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-xs tw-font-semibold tw-text-gray-700">
            {t(labels.sectionLabel)}
          </span>
          <span className="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded-full">
            {rows.filter((r) => (r.name || '').trim()).length}
          </span>
        </div>
      )}

      <div className="tw-flex tw-flex-col tw-gap-3">
        {rows.map((row, index) => (
          <div
            key={row.id}
            className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-[#FAFBFE] tw-p-3 tw-flex tw-flex-col tw-gap-3"
          >
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-xs tw-font-medium tw-text-gray-500">
                {(row.name || '').trim() || t(labels.itemLabel, { index: index + 1 })}
              </span>
              {rows.length > 1 && (
                <button
                  type="button"
                  className="tw-text-gray-400 hover:tw-text-red-500"
                  onClick={() => removeRow(row.id)}
                  aria-label={t(labels.remove)}
                >
                  <IconMC name="trash-2-outline" size="sm" />
                </button>
              )}
            </div>

            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
              <div className="tw-flex tw-flex-col tw-gap-1">
                <label
                  className="tw-text-[11px] tw-font-medium tw-text-gray-500"
                  htmlFor={`${idPrefix}_name_${row.id}`}
                >
                  {t(labels.nameLabel)}
                </label>
                <div className="tw-flex tw-gap-2 tw-items-center">
                  <input
                    id={`${idPrefix}_name_${row.id}`}
                    type="text"
                    className={floatingFieldClass}
                    value={row.name}
                    onChange={(e) => updateRow(row.id, { name: e.target.value })}
                    placeholder={t(labels.namePlaceholder)}
                  />
                  <AiDescriptionToggle
                    active={row.showDescription}
                    onClick={() => toggleDescription(row.id)}
                    title={t(labels.aiHint)}
                  />
                </div>
              </div>

              <div className="tw-flex tw-flex-col tw-gap-1">
                <label
                  className="tw-text-[11px] tw-font-medium tw-text-gray-500"
                  htmlFor={`${idPrefix}_value_${row.id}`}
                >
                  {t(labels.valueLabel)}
                </label>
                <input
                  id={`${idPrefix}_value_${row.id}`}
                  type="text"
                  className={`${floatingFieldClass} ${row.showDescription ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                  value={row.value}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  placeholder={row.showDescription ? t(labels.aiValuePlaceholder) : t(labels.valuePlaceholder)}
                  disabled={row.showDescription}
                />
              </div>
            </div>

            {row.showDescription && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <label
                  className="tw-text-[11px] tw-font-medium tw-text-gray-500"
                  htmlFor={`${idPrefix}_desc_${row.id}`}
                >
                  {t(labels.descriptionLabel)}
                </label>
                <textarea
                  id={`${idPrefix}_desc_${row.id}`}
                  className={`${floatingFieldClass} tw-min-h-[72px] tw-resize-y`}
                  value={row.description}
                  onChange={(e) => updateRow(row.id, { description: e.target.value })}
                  placeholder={t(labels.descriptionPlaceholder)}
                />
                <p className="tw-text-[10px] tw-text-gray-400">
                  {t(labels.descriptionHint)}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-py-2.5 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-bg-blue-50/50 tw-transition-colors"
        onClick={addRow}
      >
        <IconMC name="add-outline" size="sm" />
        {t(labels.add)}
      </button>
    </div>
  );
};

export default RetailerApiRequestRowsEditor;
